import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/api/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
      if (e?.response?.data?.detail === "demo_expired" && /^\/(demo\/app|shop\/)/.test(window.location.pathname)) window.location.assign("/demo-expired");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (formData) => {
    const { data } = await api.post("/api/auth/register", formData);
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post("/api/auth/logout");
    setUser(false);
  };

  const viewAs = async (userId) => {
    const { data } = await api.post(`/api/admin/impersonate/${userId}`);
    await checkAuth();
    return data;
  };

  const exitViewAs = async () => {
    await api.post("/api/admin/impersonate-stop");
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, viewAs, exitViewAs }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
